import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sheet as SheetIcon, Loader2, RefreshCw, CheckCircle2, AlertCircle, Copy } from 'lucide-react';
import { Sidebar } from '../../components/Sidebar';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const cfg = { withCredentials: true };
const CARD = 'bg-white dark:bg-[#0F1115] border border-black/[0.08] dark:border-white/5 rounded-2xl p-6';
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export const googleRedirectUri = () => `${window.location.origin}/admin/google/callback`;

const STEPS = [
  'Open console.cloud.google.com and pick the project that owns the Vyvre OAuth client (signed in as the project owner).',
  'Go to "Google Auth Platform" → "Audience" (older consoles: "APIs & Services" → "OAuth consent screen").',
  'Under "Publishing status" click "Publish app" and confirm. Status changes from Testing to In production.',
  'Google may show an "unverified app" notice because Sheets/Drive are sensitive scopes. That is fine for an internal tool — when reconnecting, click "Advanced" → "Go to Vyvre (unsafe)". Verification is only needed if outside users must sign in.',
  'Go to "Clients" (or "Credentials"), open the Web OAuth client and add the redirect link shown on this page to "Authorized redirect URIs". Add the live-site version too (same path on your live domain). Save.',
  'Come back here and click "Reconnect Google" once. After publishing, the new token no longer expires every 7 days.',
];

const Step = ({ n, text }) => (
  <li className="flex gap-3 text-sm text-[#374151] dark:text-zinc-300"><span className="h-6 w-6 shrink-0 rounded-full bg-[#FF451A]/10 text-[#FF451A] text-xs font-bold flex items-center justify-center">{n}</span><span>{text}</span></li>
);

export default function AdminGoogle() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { setS(null); axios.get(`${API_URL}/api/admin/google/status`, cfg).then((r) => setS(r.data)).catch(() => setS({ connected: false, error: 'Could not check status' })); }, []);
  useEffect(load, [load]);
  const reconnect = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/google/start`, { redirect_uri: googleRedirectUri() }, cfg);
      window.location.href = data.auth_url;
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start Google sign-in'); setBusy(false); }
  };
  const copy = () => navigator.clipboard.writeText(googleRedirectUri()).then(() => toast.success('Redirect link copied'));

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D] min-w-0" data-testid="admin-google-page">
        <div className="max-w-[900px] mx-auto space-y-6">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-[#0F0F0F] dark:text-white mb-2 flex items-center gap-3"><SheetIcon className="text-[#0F9D58]" size={30} /> Google Sheets Connection</h1>
            <p className="text-[#6B6B6B] dark:text-zinc-400">All sheet exports are created in the Drive of {s?.expected_email || 'vyvreplatform@gmail.com'}.</p>
          </div>
          <div className={CARD} data-testid="google-status-card">
            {!s ? <Loader2 className="animate-spin text-[#FF451A]" size={18} /> : (
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-3">
                  {s.connected ? <CheckCircle2 className="text-[#00C972] mt-0.5" size={22} /> : <AlertCircle className="text-[#FF3B30] mt-0.5" size={22} />}
                  <div>
                    <div className="font-semibold text-[#0F0F0F] dark:text-white" data-testid="google-status-text">{s.connected ? 'Connected — exports are working' : 'Not connected — exports will fail'}</div>
                    <div className="text-xs text-[#6B6B6B] dark:text-zinc-400 mt-1">{s.email}{s.error ? ` · ${s.error}` : ''}{s.reconnected_at ? ` · reconnected ${new Date(s.reconnected_at).toLocaleString()} by ${s.reconnected_by || 'admin'}` : ''}</div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={load} className="rounded-full px-4 py-2 text-xs font-medium inline-flex items-center gap-1.5 border border-black/[0.12] dark:border-white/[0.08] hover:border-[#FF451A]/40 transition-colors" data-testid="google-status-refresh"><RefreshCw size={12} /> Check again</button>
                  <button onClick={reconnect} disabled={busy} className="rounded-full px-4 py-2 text-xs font-semibold inline-flex items-center gap-1.5 bg-[#0F9D58] hover:bg-[#0B8043] text-white disabled:opacity-50 transition-colors" data-testid="google-reconnect-btn">{busy ? <Loader2 size={12} className="animate-spin" /> : <SheetIcon size={12} />} Reconnect Google</button>
                </div>
              </div>
            )}
          </div>
          <div className={CARD}>
            <div className="text-xs font-semibold uppercase tracking-[0.08em] text-[#0F0F0F] dark:text-white mb-2">Redirect link for this site</div>
            <p className="text-xs text-[#6B6B6B] dark:text-zinc-400 mb-3">Add this exact link under "Authorized redirect URIs" on the Google OAuth client before reconnecting.</p>
            <div className="flex items-center gap-2"><code className="flex-1 text-xs bg-black/[0.04] dark:bg-white/5 rounded-lg px-3 py-2 break-all" data-testid="google-redirect-uri">{googleRedirectUri()}</code>
              <button onClick={copy} className="rounded-full p-2 border border-black/[0.12] hover:text-[#FF451A] hover:border-[#FF451A]/50 transition-colors" data-testid="google-redirect-copy"><Copy size={13} /></button></div>
          </div>
          <div className={CARD} data-testid="google-publish-steps">
            <div className="text-xs font-semibold uppercase tracking-[0.08em] text-[#0F0F0F] dark:text-white mb-4">Stop the 7-day expiry: publish the Google Cloud app</div>
            <ol className="space-y-3">{STEPS.map((t, i) => <Step key={i} n={i + 1} text={t} />)}</ol>
          </div>
        </div>
      </div>
    </div>
  );
}
